import { useEffect, useState } from 'react';
import ClubCard from '../../components/club/ClubCard';
import { supabase } from '../../lib/supabaseClient';
import BackButton from '../../components/common/BackButton';
import ClubCreateForm from '../../components/club/ClubCreateForm';
import './ClubList.css';

// 동아리 목록 페이지 컴포넌트
export default function ClubList() {
    // 동아리 목록 저장
    const [clubs, setClubs] = useState([]);

    // 동아리 생성 폼 표시 여부
    const [showForm, setShowForm] = useState(false);

    // 선택된 카테고리
    const [selectedCategory, setSelectedCategory] = useState('전체');

    // 내가 가입한 동아리 id 목록
    const [memberClubIds, setMemberClubIds] = useState([]);

    // 카테고리 필터 목록
    const categories = ['전체', '중앙', '비중앙', '음악', '댄스', '스포츠', '과학', '코딩', '봉사', '사회', '게임', '종교', '스터디', '학술'];

    // 동아리 추가 버튼 클릭 시 로그인 여부 확인
    async function handleCreateClick() {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            alert('로그인을 해주세요!');
            return;
        }

        setShowForm(true);
    }

    // 가입 여부와 모집 상태 기준으로 동아리 정렬
    const sortedClubs = [...clubs].sort((a, b) => {
        const aMember = memberClubIds.includes(a.id);
        const bMember = memberClubIds.includes(b.id);

        // 1순위: 내가 가입한 동아리
        if (aMember && !bMember) return -1;
        if (!aMember && bMember) return 1;

        // 2순위: 모집 중
        if (a.recruitment_status === '모집 중' && b.recruitment_status !== '모집 중') return -1;
        if (a.recruitment_status !== '모집 중' && b.recruitment_status === '모집 중') return 1;

        // 3순위: 모집 종료
        return 0;
    });

    // 선택된 카테고리에 따라 동아리 필터링
    const filteredClubs = selectedCategory === '전체'
        ? sortedClubs
        : sortedClubs.filter(club => {
            if (!club.category) return false;

            if (Array.isArray(club.category)) {
                return club.category.some(c => c === selectedCategory);
            }

            try {
                const parsed = JSON.parse(club.category);
                return parsed.some(c => c === selectedCategory);
            } catch {
                return club.category === selectedCategory;
            }
        });

    // 내가 가입한 동아리 목록 조회
    useEffect(() => {
        async function getMemberClubs() {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                setMemberClubIds([]);
                return;
            }

            // users 테이블에서 사용자 id 조회
            const { data: userData, error: userError } = await supabase
                .from('users')
                .select('id')
                .eq('email', user.email)
                .maybeSingle();

            if (userError) {
                console.log(userError);
                return;
            }

            if (!userData) return;

            // club_members 테이블에서 가입한 동아리 id 조회
            const { data, error } = await supabase
                .from('club_members')
                .select('club_id')
                .eq('user_id', userData.id);

            if (error) {
                console.log(error);
                return;
            }

            if (data) {
                setMemberClubIds(data.map(member => member.club_id));
            }
        }

        getMemberClubs();
    }, []);

    // 동아리 목록 조회 및 모집 상태 갱신
    useEffect(() => {
        let ignore = false;

        async function getClubs() {
            const { data, error } = await supabase
                .from('clubs')
                .select('*')
                .neq('approval_status', '대기');

            if (error) {
                console.log(error);
                return;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // 모집 기간 기준으로 각 동아리 상태 갱신
            const updatedData = await Promise.all(
                data.map(async (club) => {
                    if (!club.recruitment_start || !club.recruitment_end) {
                        if (club.recruitment_status !== '모집 종료') {
                            await supabase
                                .from('clubs')
                                .update({ recruitment_status: '모집 종료' })
                                .eq('id', club.id);
                        }

                        return {
                            ...club,
                            recruitment_status: '모집 종료',
                        };
                    }

                    const start = new Date(club.recruitment_start);
                    const end = new Date(club.recruitment_end);

                    start.setHours(0, 0, 0, 0);
                    end.setHours(0, 0, 0, 0);

                    const newStatus = today >= start && today <= end
                        ? '모집 중'
                        : '모집 종료';

                    if (newStatus !== club.recruitment_status) {
                        await supabase
                            .from('clubs')
                            .update({ recruitment_status: newStatus })
                            .eq('id', club.id);
                    }

                    return {
                        ...club,
                        recruitment_status: newStatus,
                    };
                })
            );

            if (!ignore) {
                setClubs(updatedData);
            }
        }

        getClubs();

        // 동아리 목록을 10초마다 갱신
        const interval = setInterval(getClubs, 10000);

        return () => {
            ignore = true;
            clearInterval(interval);
        };
    }, []);

    return (
        <div className="club-list-wrapper">
            <BackButton />

            {/* 동아리 목록 상단 영역 */}
            <div className="club-list-header">
                <div className="club-list-title-row">
                    <h2 className="club-list-title">동아리({clubs.length})</h2>

                    {/* 카테고리 필터 버튼 */}
                    <div className="club-category-filters">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`category-filter ${selectedCategory === cat ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 동아리 추가 버튼 */}
                <button className="club-create" onClick={handleCreateClick}>
                    + 동아리 추가
                </button>
            </div>

            {/* 동아리 카드 목록 */}
            <div className="club-list">
                {filteredClubs.map((club) => (
                    <ClubCard
                        key={club.id}
                        club={club}
                        isMember={memberClubIds.includes(club.id)}
                    />
                ))}
            </div>

            {/* 동아리 생성 모달 */}
            {showForm && (
                <div className="modal-bg">
                    <div className="modal-content">
                        <ClubCreateForm onClose={() => setShowForm(false)} />
                    </div>
                </div>
            )}
        </div>
    );
}